/**
 * @fileoverview Contact Support Modal Component
 * 
 * @description
 * Modal form for authenticated users to contact technical support.
 * Allows users to submit support tickets with categorized issues.
 * 
 * @component ContactSupportModal
 * @version 1.0.0
 * @date 2026-01-10
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import { 
  FaHeadset, 
  FaBug, 
  FaQuestionCircle, 
  FaLightbulb, 
  FaExclamationTriangle,
  FaCog
} from 'react-icons/fa';

const ContactSupportModal = ({ show, onHide }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    category: '',
    priority: 'medium',
    subject: '',
    description: '',
    module: '',
    attachmentUrl: '',
    allowRemoteAccess: false
  });

  const [validated, setValidated] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setFormData({
        category: '',
        priority: 'medium',
        subject: '',
        description: '',
        module: '',
        attachmentUrl: '',
        allowRemoteAccess: false
      });
      setValidated(false);
      setSubmitStatus(null);
      setIsSubmitting(false);
    }
  }, [show]);

  // Support categories with icons
  const categories = [
    { value: 'technical', label: t('contactSupport.categories.technical') || 'Technical Issue', icon: FaCog },
    { value: 'bug', label: t('contactSupport.categories.bug') || 'Bug Report', icon: FaBug },
    { value: 'question', label: t('contactSupport.categories.question') || 'General Question', icon: FaQuestionCircle },
    { value: 'feature', label: t('contactSupport.categories.feature') || 'Feature Request', icon: FaLightbulb },
    { value: 'access', label: t('contactSupport.categories.access') || 'Access/Permission Issue', icon: FaExclamationTriangle },
    { value: 'other', label: t('contactSupport.categories.other') || 'Other', icon: FaHeadset }
  ];

  // Application modules
  const modules = [
    { value: 'projects', label: 'Projects' },
    { value: 'survey', label: 'Survey' },
    { value: 'drilling', label: 'Drilling' },
    { value: 'recording', label: 'Recording' },
    { value: 'basecamp', label: 'Base Camp' },
    { value: 'hr', label: 'HR / Personnel' },
    { value: 'inventory', label: 'Inventory' },
    { value: 'qhse', label: 'QHSE' },
    { value: 'finance', label: 'Finance' },
    { value: 'reports', label: 'Reports' },
    { value: 'settings', label: 'Settings' },
    { value: 'general', label: 'General / Login' },
    { value: 'other', label: 'Other' }
  ];

  // Priority levels
  const priorities = [
    { value: 'low', label: t('contactSupport.priorities.low') || 'Low', color: 'var(--archon-bs-success)' },
    { value: 'medium', label: t('contactSupport.priorities.medium') || 'Medium', color: 'var(--archon-warning)' },
    { value: 'high', label: t('contactSupport.priorities.high') || 'High', color: 'var(--archon-cat-orange)' },
    { value: 'critical', label: t('contactSupport.priorities.critical') || 'Critical', color: 'var(--archon-danger)' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Build the support request payload
      const payload = {
        ...formData,
        userName: user?.full_name || user?.username || 'Unknown User',
        userEmail: user?.email || '',
        userRole: user?.role || '',
        companyName: user?.company_name || ''
      };

      const response = await api.post('/contact/support', payload);
      
      if (response.data.success) {
        setSubmitStatus('success');
        
        // Close modal after 3 seconds
        setTimeout(() => {
          onHide();
          setFormData({
            category: '',
            priority: 'medium',
            subject: '',
            description: '',
            module: '',
            attachmentUrl: '',
            allowRemoteAccess: false
          });
          setSubmitStatus(null);
          setValidated(false);
          setIsSubmitting(false);
        }, 3000);
      } else {
        setSubmitStatus('error');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ backgroundColor: 'var(--archon-navy-deep)', color: 'white' }}>
        <Modal.Title className="d-flex align-items-center">
          <FaHeadset className="me-2" />
          {t('contactSupport.title') || 'Contact Support'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ backgroundColor: 'var(--archon-landing-light-surface)', maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Success Alert */}
        {submitStatus === 'success' && (
          <Alert variant="success">
            <Alert.Heading>{t('contactSupport.successTitle') || 'Ticket Submitted!'}</Alert.Heading>
            {t('contactSupport.successMessage') || 'Your support request has been submitted successfully. Our team will review it and respond within 24-48 hours.'}
          </Alert>
        )}
        
        {/* Error Alert */}
        {submitStatus === 'error' && (
          <Alert variant="danger">
            {t('contactSupport.errorMessage') || 'Sorry, there was an error submitting your request. Please try again or email support@archonpm.com directly.'}
          </Alert>
        )}

        {/* Info Card */}
        <Alert variant="info" className="mb-3">
          <small>
            <strong>{t('contactSupport.infoTitle') || 'Need Help?'}</strong>{' '}
            {t('contactSupport.infoText') || 'Fill out this form to submit a support ticket. For urgent issues, mark the priority as Critical.'}
          </small>
        </Alert>

        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {/* Category Selection */}
          <h6 className="mb-3" style={{ color: 'var(--archon-navy-deep)', fontWeight: 600 }}>
            {t('contactSupport.sections.issueType') || 'Issue Type'}
          </h6>
          
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('contactSupport.fields.category') || 'Category'} *</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {categories.map(cat => {
                    const IconComponent = cat.icon;
                    return (
                      <Button
                        key={cat.value}
                        type="button"
                        variant={formData.category === cat.value ? 'primary' : 'outline-secondary'}
                        className="d-flex align-items-center"
                        style={{
                          backgroundColor: formData.category === cat.value ? 'var(--archon-blue)' : 'transparent',
                          borderColor: formData.category === cat.value ? 'var(--archon-blue)' : 'var(--archon-bs-secondary-gray)'
                        }}
                        onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                      >
                        <IconComponent className="me-2" />
                        {cat.label}
                      </Button>
                    );
                  })}
                </div>
                {validated && !formData.category && (
                  <Form.Text className="text-danger">
                    {t('contactSupport.validation.selectCategory') || 'Please select a category'}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Module and Priority */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('contactSupport.fields.module') || 'Related Module'}</Form.Label>
                <Form.Select
                  name="module"
                  value={formData.module}
                  onChange={handleChange}
                >
                  <option value="">{t('contactSupport.placeholders.selectModule') || 'Select a module...'}</option>
                  {modules.map(mod => (
                    <option key={mod.value} value={mod.value}>{mod.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('contactSupport.fields.priority') || 'Priority'} *</Form.Label>
                <Form.Select
                  required
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  {priorities.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Subject */}
          <h6 className="mb-3 mt-4" style={{ color: 'var(--archon-navy-deep)', fontWeight: 600 }}>
            {t('contactSupport.sections.details') || 'Details'}
          </h6>
          
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('contactSupport.fields.subject') || 'Subject'} *</Form.Label>
                <Form.Control
                  required
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t('contactSupport.placeholders.subject') || 'Brief summary of your issue'}
                  maxLength={200}
                />
                <Form.Control.Feedback type="invalid">
                  {t('contactSupport.validation.required') || 'This field is required'}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Description */}
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('contactSupport.fields.description') || 'Description'} *</Form.Label>
                <Form.Control
                  required
                  as="textarea"
                  rows={5}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={t('contactSupport.placeholders.description') || 'Please describe your issue in detail. Include steps to reproduce if applicable...'}
                />
                <Form.Control.Feedback type="invalid">
                  {t('contactSupport.validation.required') || 'This field is required'}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {t('contactSupport.hints.description') || 'The more details you provide, the faster we can help you.'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Screenshot/Attachment URL */}
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('contactSupport.fields.attachmentUrl') || 'Screenshot/Attachment URL'}</Form.Label>
                <Form.Control
                  type="url"
                  name="attachmentUrl"
                  value={formData.attachmentUrl}
                  onChange={handleChange}
                  placeholder={t('contactSupport.placeholders.attachmentUrl') || 'https://drive.google.com/... or similar link'}
                />
                <Form.Text className="text-muted">
                  {t('contactSupport.hints.attachmentUrl') || 'Optional: Paste a link to screenshots or files (Google Drive, Dropbox, etc.)'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Remote Access Permission */}
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="allowRemoteAccess"
                  checked={formData.allowRemoteAccess}
                  onChange={handleChange}
                  label={
                    <span>
                      {t('contactSupport.fields.allowRemoteAccess') || 'Allow support team to access my account for troubleshooting'}
                      <small className="text-muted d-block">
                        {t('contactSupport.hints.remoteAccess') || 'This helps us diagnose issues faster. Access is logged and secure.'}
                      </small>
                    </span>
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          {/* User Info Display */}
          <div className="bg-light p-3 rounded mb-3 border">
            <small className="text-muted">
              <strong>{t('contactSupport.userInfo') || 'Your Information'}:</strong><br />
              {user?.full_name || user?.username} ({user?.email})<br />
              {user?.company_name && <span>{user.company_name}</span>}
            </small>
          </div>

          <Modal.Footer style={{ backgroundColor: 'var(--archon-landing-light-surface)', border: 'none', paddingLeft: 0, paddingRight: 0 }}>
            <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
              {t('contactSupport.buttons.cancel') || 'Cancel'}
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              style={{ backgroundColor: 'var(--archon-blue)', borderColor: 'var(--archon-blue)' }}
              disabled={submitStatus === 'success' || isSubmitting || !formData.category}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('contactSupport.buttons.sending') || 'Sending...'}
                </>
              ) : (
                t('contactSupport.buttons.submit') || 'Submit Ticket'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ContactSupportModal;
